import React, {
   createContext,
   useContext,
   useEffect,
   useMemo,
   useState,
} from "react";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";

const OidcContext = createContext({
   isAuthenticated: false,
   isLoading: true,
   login: async () => {},
   logout: async () => {},
   oidcUser: null,
   userOidc: null,
});

let oidcManager = null;
let oidcManagerKey = null;

const CALLBACK_PATH = "/authentication/callback";
const SILENT_CALLBACK_PATH = "/authentication/silent-callback";

const toArray = (value) => {
   if (Array.isArray(value)) {
      return value;
   }

   return value ? [value] : [];
};

const adaptUser = (user) => {
   if (!user) {
      return null;
   }

   const profile = user.profile || {};
   const roles = toArray(
      profile.role || profile.roles || user.role || user.roles
   );

   return {
      ...user,
      ...profile,
      profile,
      info: profile,
      sub: profile.sub || user.sub || null,
      email: profile.email || profile.preferred_username || user.email || null,
      preferred_username:
         profile.preferred_username ||
         profile.email ||
         user.preferred_username ||
         null,
      picture: profile.picture || user.picture || null,
      role: profile.role || profile.roles || user.role || [],
      roles,
   };
};

const buildConfigurationKey = (configuration = {}) =>
   JSON.stringify({
      authority: configuration.authority,
      client_id: configuration.client_id,
      redirect_uri: configuration.redirect_uri,
      silent_redirect_uri: configuration.silent_redirect_uri,
      post_logout_redirect_uri: configuration.post_logout_redirect_uri,
      scope: configuration.scope,
   });

const createManager = (configuration = {}) => {
   if (typeof window === "undefined") {
      return null;
   }

   return new UserManager({
      authority: configuration.authority,
      client_id: configuration.client_id,
      redirect_uri: configuration.redirect_uri,
      silent_redirect_uri: configuration.silent_redirect_uri,
      post_logout_redirect_uri: configuration.post_logout_redirect_uri,
      scope: configuration.scope,
      response_type: "code",
      automaticSilentRenew: true,
      monitorSession: false,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
   });
};

export const initializeOidc = (configuration = {}) => {
   const nextKey = buildConfigurationKey(configuration);

   if (!oidcManager || oidcManagerKey !== nextKey) {
      oidcManager = createManager(configuration);
      oidcManagerKey = nextKey;
   }

   return oidcManager;
};

export const getOidcManager = () => oidcManager;

export const getOidcUser = async () => {
   if (!oidcManager) {
      return null;
   }

   const user = await oidcManager.getUser();
   return adaptUser(user);
};

export const getAccessToken = async () => {
   if (!oidcManager) {
      return null;
   }

   let user = await oidcManager.getUser();

   if (user?.expired) {
      try {
         user = await oidcManager.signinSilent();
      } catch (error) {
         console.warn("Silent renew failed when reading access token", error);
      }
   }

   return user?.access_token || null;
};

export const renewTokens = async () => {
   if (!oidcManager) {
      return null;
   }

   const user = await oidcManager.signinSilent();
   return adaptUser(user);
};

export const OidcProvider = ({
   configuration,
   withCustomHistory,
   loadingComponent: LoadingComponent,
   authenticatingComponent: AuthenticatingComponent,
   callbackSuccessComponent: CallbackSuccessComponent,
   children,
}) => {
   const configurationKey = useMemo(
      () => buildConfigurationKey(configuration),
      [configuration]
   );
   const [state, setState] = useState({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isProcessingCallback: false,
   });

   useEffect(() => {
      const manager = initializeOidc(configuration);

      if (!manager || typeof window === "undefined") {
         return undefined;
      }

      let isMounted = true;

      const navigate = (targetPath) => {
         const history =
            typeof withCustomHistory === "function"
               ? withCustomHistory()
               : null;
         const destination = targetPath || "/";

         if (history?.replaceState) {
            history.replaceState(destination);
            return;
         }

         window.location.replace(destination);
      };

      const syncUser = async () => {
         const loadedUser = await manager.getUser();

         if (!isMounted) {
            return;
         }

         setState({
            user: adaptUser(loadedUser),
            isAuthenticated: Boolean(loadedUser && !loadedUser.expired),
            isLoading: false,
            isProcessingCallback: false,
         });
      };

      const initialize = async () => {
         try {
            await manager.clearStaleState();
            const currentPath = window.location.pathname;
            const isSilentCallback = currentPath.includes(SILENT_CALLBACK_PATH);
            const isRedirectCallback =
               currentPath.includes(CALLBACK_PATH) && !isSilentCallback;

            if (isSilentCallback) {
               if (isMounted) {
                  setState((prevState) => ({
                     ...prevState,
                     isLoading: true,
                     isProcessingCallback: true,
                  }));
               }

               await manager.signinSilentCallback();
               await syncUser();
               return;
            }

            if (isRedirectCallback) {
               if (isMounted) {
                  setState((prevState) => ({
                     ...prevState,
                     isLoading: true,
                     isProcessingCallback: true,
                  }));
               }

               const callbackUser = await manager.signinRedirectCallback();
               navigate(callbackUser?.state?.returnTo || "/");
               return;
            }

            await syncUser();
         } catch (error) {
            console.error("OIDC initialization failed", error);

            if (!isMounted) {
               return;
            }

            setState({
               user: null,
               isAuthenticated: false,
               isLoading: false,
               isProcessingCallback: false,
            });

            if (window.location.pathname.includes(CALLBACK_PATH)) {
               navigate("/");
            }
         }
      };

      const handleUserLoaded = (loadedUser) => {
         if (!isMounted) {
            return;
         }

         setState({
            user: adaptUser(loadedUser),
            isAuthenticated: Boolean(loadedUser && !loadedUser.expired),
            isLoading: false,
            isProcessingCallback: false,
         });
      };

      const handleUserUnloaded = () => {
         if (!isMounted) {
            return;
         }

         setState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isProcessingCallback: false,
         });
      };

      manager.events.addUserLoaded(handleUserLoaded);
      manager.events.addUserUnloaded(handleUserUnloaded);
      manager.events.addUserSignedOut(handleUserUnloaded);

      initialize();

      return () => {
         isMounted = false;
         manager.events.removeUserLoaded(handleUserLoaded);
         manager.events.removeUserUnloaded(handleUserUnloaded);
         manager.events.removeUserSignedOut(handleUserUnloaded);
      };
   }, [configuration, configurationKey, withCustomHistory]);

   const contextValue = useMemo(() => {
      const login = async (returnTo) => {
         const manager = getOidcManager();

         if (!manager) {
            return;
         }

         const targetPath =
            typeof returnTo === "string"
               ? returnTo
               : `${window.location.pathname}${window.location.search}${window.location.hash}`;

         return manager.signinRedirect({ state: { returnTo: targetPath } });
      };

      const logout = async () => {
         const manager = getOidcManager();

         if (!manager) {
            return;
         }

         return manager.signoutRedirect();
      };

      return {
         isAuthenticated: state.isAuthenticated,
         isLoading: state.isLoading,
         login,
         logout,
         oidcUser: state.user,
         userOidc: state.user,
      };
   }, [state]);

   if (state.isProcessingCallback && CallbackSuccessComponent) {
      return <CallbackSuccessComponent />;
   }

   if (state.isLoading && AuthenticatingComponent) {
      return <AuthenticatingComponent />;
   }

   if (state.isLoading && LoadingComponent) {
      return <LoadingComponent />;
   }

   return (
      <OidcContext.Provider value={contextValue}>
         {children}
      </OidcContext.Provider>
   );
};

export const useOidc = () => useContext(OidcContext);

export const useOidcUser = () => {
   const context = useContext(OidcContext);

   return {
      oidcUser: context.oidcUser,
      userOidc: context.userOidc,
   };
};
